import { createFileRoute } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Instagram, Play } from "lucide-react";

import { listQuery } from "@/lib/queries";
import { PageHero, Section, EmptyState } from "@/components/site/bits";
import { videoEmbed } from "@/lib/format";

export const Route = createFileRoute("/videos")({
  head: () => ({
    meta: [
      { title: "Vídeos | Professor Rurdiney — 40123" },
      {
        name: "description",
        content: "Assista aos vídeos da campanha e das ações do Professor Rurdiney, candidato a vereador nº 40123.",
      },
      { property: "og:title", content: "Vídeos | Professor Rurdiney — 40123" },
      {
        property: "og:description",
        content: "Vídeos da campanha e das ações comunitárias do Professor Rurdiney.",
      },
    ],
  }),
  loader: ({ context }) => context.queryClient.ensureQueryData(listQuery("videos")),
  component: VideosPage,
});

function VideosPage() {
  const { data: videos } = useSuspenseQuery(listQuery("videos"));

  return (
    <>
      <PageHero
        eyebrow="Multimídia"
        title="Vídeos"
        description="Confira os vídeos da campanha, discursos e registros das ações pela cidade."
      />

      <Section>
        {videos.length === 0 ? (
          <EmptyState message="Nenhum vídeo publicado ainda." />
        ) : (
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {videos.map((v: any) => {
              const embed = videoEmbed(v.video_url);
              return (
                <article
                  key={v.id}
                  className="overflow-hidden rounded-2xl border border-border bg-card shadow-card"
                >
                  {embed ? (
                    <div
                      className={
                        embed.provider === "instagram"
                          ? "aspect-[4/5] w-full bg-background"
                          : "aspect-video w-full bg-primary-deep"
                      }
                    >
                      <iframe
                        src={embed.src}
                        title={v.title}
                        loading="lazy"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        scrolling="no"
                        frameBorder={0}
                        className="size-full"
                      />
                    </div>
                  ) : (
                    <a
                      href={v.video_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex aspect-video w-full items-center justify-center bg-primary-deep text-primary-foreground"
                    >
                      <Play className="size-10" />
                    </a>
                  )}
                  <div className="p-5">
                    <h3 className="text-lg text-foreground">{v.title}</h3>
                    {v.description && (
                      <p className="mt-1 text-sm text-muted-foreground">{v.description}</p>
                    )}
                    {v.video_url && (
                      <a
                        href={v.video_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-primary hover:text-accent-deep"
                      >
                        <Instagram className="size-4" /> Ver no Instagram
                      </a>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </Section>
    </>
  );
}
